// BotForm.tsx - COMPLETE FILE with container support
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { createBot, updateBot, getBotById } from '../../services/botService';
import { getTemplateById } from '../../services/templates';
import UniversalTemplateSelector from '../createWhatsappBroadcast/UnifiedTemplateSelector';
import WhatsAppPreview from '../template/createTemplate/WhatsAppPreview';
import Header from '../../components/header';
import { Container } from 'reactstrap';
import styles from './BotForm.module.css';
import logger from '../../utils/logger';

interface BotFormProps {
  isEdit?: boolean;
  botId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  hideHeader?: boolean;
  // 🔥 NEW: Callback to notify container about form state
  onFormStateChange?: (isValid: boolean, isSaving: boolean) => void;
}

interface FormData {
  name: string;
  description: string;
  keywords: string[];
  selectedTemplate: any;
  isActive: boolean;
}

interface TemplatePreviewData {
  bodyValue: string;
  values: {
    header: string;
    header_value: string;
    footer?: string;
    haveButtons: "yes" | "no";
    body?: string;
    buttons?: Array<{
      type: string;
      text: string;
      url?: string;
      phone_number?: string;
    }>;
  };
  file: File | null;
}

// 🔥 CRITICAL FIX: Custom comparison function for React.memo
const BotFormPropsAreEqual = (prevProps: BotFormProps, nextProps: BotFormProps) => {
  return (
    prevProps.isEdit === nextProps.isEdit &&
    prevProps.botId === nextProps.botId &&
    prevProps.hideHeader === nextProps.hideHeader
    // 🔥 IMPORTANT: Don't compare function props as they may be recreated
    // onSuccess and onCancel are likely recreated on parent re-renders
  );
};

const BotForm: React.FC<BotFormProps> = React.memo(({ 
  isEdit = false, 
  botId, 
  onSuccess, 
  onCancel,
  hideHeader = false,
  onFormStateChange // 🔥 NEW: State change callback
}) => {
  logger.log('🤖 BotForm: Rendered - COMPLETE VERSION with container support:', { isEdit, botId, hideHeader });

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    keywords: [],
    selectedTemplate: null,
    isActive: true
  });

  // UI states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  
  // Keyword management states
  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const keywordInputRef = useRef<HTMLInputElement>(null);

  // Template preview state
  const [templatePreviewData, setTemplatePreviewData] = useState<TemplatePreviewData | null>(null);

  // 🔥 CRITICAL FIX: Track what we've loaded to prevent re-loading
  const [loadedBotId, setLoadedBotId] = useState<string | null>(null);
  const [templateCache, setTemplateCache] = useState<Record<string, any>>({});

  // 🔥 DEBUG: Simplified render tracking
  const renderCount = useRef(0);
  renderCount.current += 1;
  logger.log(`🔄 BotForm RENDER #${renderCount.current}:`, { 
    keywordInputLength: keywordInput.length,
    formNameLength: formData.name.length,
    timestamp: Date.now() % 10000 // Last 4 digits for easy tracking
  });

  // 🔥 NEW: Compute if form is valid for container
  const isFormValid = useMemo(() => {
    return (
      formData.name.trim().length > 0 &&
      formData.keywords.length > 0 &&
      formData.selectedTemplate !== null
    );
  }, [formData.name, formData.keywords.length, formData.selectedTemplate]);

  // 🔥 NEW: Notify container about form state changes
  useEffect(() => {
    if (onFormStateChange) {
      logger.log('📊 BotForm: Notifying container of state change:', { isFormValid, saving });
      onFormStateChange(isFormValid, saving);
    }
  }, [isFormValid, saving, onFormStateChange]);

  // 🔥 OPTIMIZED: Transform template data (STABLE reference)
  const transformTemplateForPreview = useCallback((template: any): TemplatePreviewData => {
    const components = template.components || [];
    
    if (components.length === 0) {
      return {
        bodyValue: `Preview for template: ${template.name}`,
        values: {
          header: 'none',
          header_value: '',
          footer: 'Template preview loading...',
          haveButtons: "no",
          body: `Template: ${template.name}`,
          buttons: []
        },
        file: null
      };
    }
    
    const headerComponent = components.find((c: any) => c.type === 'HEADER');
    const bodyComponent = components.find((c: any) => c.type === 'BODY');
    const footerComponent = components.find((c: any) => c.type === 'FOOTER');
    const buttonsComponent = components.find((c: any) => c.type === 'BUTTONS');

    let bodyText = bodyComponent?.text || '';
    const sampleValues = ['John Doe', 'ABC123', '15 minutes', '$99.99', 'Premium Plan'];
    bodyText = bodyText.replace(/\{\{(\d+)\}\}/g, (match: string, number: string) => {
      const index = parseInt(number) - 1;
      return sampleValues[index] || `Sample ${number}`;
    });

    return {
      bodyValue: bodyText,
      values: {
        header: headerComponent?.format?.toLowerCase() || 'none',
        header_value: headerComponent?.text || '',
        footer: footerComponent?.text || '',
        haveButtons: (buttonsComponent?.buttons || []).length > 0 ? "yes" : "no",
        body: bodyText,
        buttons: buttonsComponent?.buttons || []
      },
      file: null
    };
  }, []); // 🔥 EMPTY DEPS: This function is pure and doesn't depend on state

  const detectPersonalizationFields = (template: any): { hasFields: boolean; fields: string[] } => {
  logger.log('🔍 Detecting personalization fields in template:', template?.name);
  
  if (!template || !template.components) {
    logger.log('❌ No template or components found');
    return { hasFields: false, fields: [] };
  }

  // Find body component
  const bodyComponent = template.components.find((c: any) => c.type === 'BODY');
  if (!bodyComponent || !bodyComponent.text) {
    logger.log('❌ No body component or text found');
    return { hasFields: false, fields: [] };
  }

  logger.log('📝 Template body text:', bodyComponent.text);

  // Regex to find {{1}}, {{2}}, {{variable}}, etc.
  const personalizationRegex = /\{\{([^}]+)\}\}/g;
  const matches: string[] = bodyComponent.text.match(personalizationRegex) || [];
  
  // Remove duplicates and clean up
  const uniqueFields: string[] = [...new Set(matches)];
  
  logger.log('🎯 Personalization fields detected:', uniqueFields);
  
  return {
    hasFields: uniqueFields.length > 0,
    fields: uniqueFields
  };
};


  // 🔥 VALIDATION: STABLE reference
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Bot name is required';
    }

    if (formData.keywords.length === 0) {
      newErrors.keywords = 'At least one keyword is required';
    }

    if (!formData.selectedTemplate) {
      newErrors.template = 'Please select a template';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, formData.keywords.length, formData.selectedTemplate]);

  // 🔥 OPTIMIZED: Template fetching (STABLE reference)
  const fetchTemplateForPreview = useCallback(async (templateId: string) => {
    if (templateCache[templateId]) {
      logger.log('🔥 BotForm: Using cached template');
      const previewData = transformTemplateForPreview(templateCache[templateId]);
      setTemplatePreviewData(previewData);
      return;
    }

    if (templateLoading) return;

    setTemplateLoading(true);
    try {
      const response = await getTemplateById(templateId);
      const templateData = response.data.data || response.data;
      
      if (templateData) {
        setTemplateCache(prev => ({ ...prev, [templateId]: templateData }));
        
        setFormData(prev => ({
          ...prev,
          selectedTemplate: prev.selectedTemplate ? {
            ...prev.selectedTemplate,
            name: templateData.name
          } : null
        }));

        const previewData = transformTemplateForPreview(templateData);
        setTemplatePreviewData(previewData);
      }
    } catch (error) {
      console.error('❌ Template fetch error:', error);
      toast.error('Failed to load template preview');
    } finally {
      setTemplateLoading(false);
    }
  }, [templateCache, templateLoading, transformTemplateForPreview]);

  // 🔥 LOAD BOT DATA: Only when botId changes
  useEffect(() => {
    if (!isEdit || !botId || botId === loadedBotId) return;

    setLoading(true);
    getBotById(botId)
      .then(response => {
        const botData = response.data;
        if (botData) {
          setFormData({
            name: botData.name || '',
            description: botData.objective || '',
            keywords: botData.triggerCondition?.keywords || [],
            selectedTemplate: null,
            isActive: botData.status === 'active'
          });
          setLoadedBotId(botId);

          const templateId = botData.groups?.[0]?.steps?.[0]?.template;
          if (templateId) {
            setFormData(prev => ({
              ...prev,
              selectedTemplate: { _id: templateId, name: 'Loading...', components: [] }
            }));
            fetchTemplateForPreview(templateId);
          }
        }
      })
      .catch(error => {
        console.error('❌ Bot data error:', error);
        toast.error('Failed to load bot data');
      })
      .finally(() => setLoading(false));
  }, [isEdit, botId, loadedBotId, fetchTemplateForPreview]);

  // 🔥 INPUT HANDLERS: SUPER STABLE - NO DEPS that change
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log('📝 Name changing...');
    setFormData(prev => ({ ...prev, name: e.target.value }));
    setErrors(prev => ({ ...prev, name: '' }));
  }, []); // 🔥 EMPTY DEPS!

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    logger.log('📝 Description changing...');
    setFormData(prev => ({ ...prev, description: e.target.value }));
  }, []); // 🔥 EMPTY DEPS!

  const handleKeywordInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log('📝 Keyword input changing...');
    setKeywordInput(e.target.value);
  }, []); // 🔥 EMPTY DEPS!

  const handleActiveToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log('📝 Active toggle...');
    setFormData(prev => ({ ...prev, isActive: e.target.checked }));
  }, []); // 🔥 EMPTY DEPS!

  // 🔥 KEYWORD MANAGEMENT: STABLE references
  const addKeywordFromInput = useCallback(() => {
    const trimmed = keywordInput.trim().toLowerCase();
    if (trimmed) {
      setFormData(prev => {
        if (!prev.keywords.includes(trimmed)) {
          setKeywordInput('');
          setErrors(prevErrors => ({ ...prevErrors, keywords: '' }));
          return { ...prev, keywords: [...prev.keywords, trimmed] };
        }
        return prev;
      });
    }
  }, [keywordInput]); // Only keywordInput as dependency

  const removeKeyword = useCallback((keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }));
  }, []);

  const handleKeywordInputKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeywordFromInput();
    }
  }, [addKeywordFromInput]);

  // 🔥 TEMPLATE HANDLERS: STABLE references  
  const handleTemplateSelect = useCallback((template: any) => {
    setFormData(prev => ({ ...prev, selectedTemplate: template }));
    setShowTemplateSelector(false);
    if (template._id) {
      fetchTemplateForPreview(template._id);
    }
    setErrors(prev => ({ ...prev, template: '' }));
  }, [fetchTemplateForPreview]);

  const handleTemplateChange = useCallback(() => {
    setShowTemplateSelector(true);
  }, []);

  const handleTemplateDiscard = useCallback(() => {
    setShowTemplateSelector(false);
  }, []);

  // 🔥 FORM SUBMISSION: STABLE reference
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    logger.log('🔥 BotForm: Form submission triggered');
    
    if (!validateForm()) {
      logger.log('❌ BotForm: Form validation failed');
      return;
    }

    setSaving(true);
    try {
      const apiData = {
        name: formData.name.trim(),
        objective: formData.description.trim() || undefined,
        triggerCondition: { type: 'keyword' as const, keywords: formData.keywords },
        status: formData.isActive ? 'active' as const : 'inactive' as const,
        groups: formData.selectedTemplate ? [{
          id: 'template_response',
          groupName: 'Template Response',
          required: true,
          conditions: {},
          steps: [{ id: 'template_step', fieldType: 'template', template: formData.selectedTemplate._id }]
        }] : []
      };

      if (isEdit && botId) {
        await updateBot(botId, apiData as any);
      } else {
        await createBot(apiData as any);
      }

      toast.success(`Bot ${isEdit ? 'updated' : 'created'} successfully!`);
      onSuccess?.();
    } catch (error) {
      console.error('❌ Save error:', error);
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} bot`);
    } finally {
      setSaving(false);
    }
  }, [validateForm, formData, isEdit, botId, onSuccess]);

  // 🔥 NEW: Listen for external form submit events (from container)
  useEffect(() => {
    const handleExternalSubmit = (e: Event) => {
      logger.log('🔥 BotForm: External submit event received from container');
      e.preventDefault(); // Prevent default form submission
      handleSubmit(e as any); // Call our submit handler
    };

    const form = document.getElementById('bot-form');
    if (form) {
      logger.log('🔥 BotForm: Setting up external submit listener');
      form.addEventListener('submit', handleExternalSubmit);
      
      // Cleanup event listener
      return () => {
        logger.log('🔥 BotForm: Cleaning up external submit listener');
        form.removeEventListener('submit', handleExternalSubmit);
      };
    }
  }, [handleSubmit]);

  // 🔥 MEMOIZED FORM CONTENT: Prevent recreation
  const formContent = useMemo(() => (
    <div className={styles.botFormWide}>
      {/* 🔥 CONDITIONAL: Only show header when not hidden */}
      {!hideHeader && (
        <div className={styles.formHeaderWide}>
          <div>
            <h1>{isEdit ? 'Edit Bot' : 'Create New Bot'}</h1>
            <p>Set up bots that automatically send WhatsApp template messages when a client messages you with specific keywords</p>
          </div>
          <div className={styles.headerActions}>
            {onCancel && (
              <button type="button" onClick={onCancel} className={styles.cancelBtn} disabled={saving}>
                Cancel
              </button>
            )}
            <button type="submit" form="bot-form" className={styles.submitBtn} disabled={saving || templateLoading}>
              {saving ? 'Saving...' : (isEdit ? 'Update Bot' : 'Create Bot')}
            </button>
          </div>
        </div>
      )}

      <form id="bot-form" onSubmit={handleSubmit} className={styles.formWide}>
        <div className={styles.formGrid}>
          <div className={styles.leftColumn}>
            
            {/* Bot Details */}
          <div className={styles.sectionWide}>
  {/* 🔥 NEW: Header with inline checkbox */}
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  }}>
    <h3 style={{ margin: 0 }}>Bot Details</h3>
    
    {/* Inline Active Toggle */}
    <div className={styles.inlineCheckbox}>
      <input
        type="checkbox"
        checked={formData.isActive}
        onChange={handleActiveToggle}
        id="bot-active"
      />
      <label htmlFor="bot-active">Active</label>
      {formData.isActive && (
        <span className={styles.liveIndicator}>LIVE</span>
      )}
    </div>
  </div>
  
  {/* 🔥 UPDATED: Remove checkbox from fieldGrid */}
  <div className={styles.fieldGrid}>
    <div className={styles.field}>
      <label htmlFor="name">Bot Name *</label>
      <input
        id="name"
        type="text"
        value={formData.name}
        onChange={handleNameChange}
        placeholder="Enter bot name"
        className={errors.name ? styles.inputError : ''}
      />
      {errors.name && <span className={styles.errorText}>{errors.name}</span>}
    </div>
    
    {/* 🔥 REMOVED: The checkbox field is now in the header */}
  </div>
  
  <div className={styles.field}>
    <label htmlFor="description">Description</label>
    <textarea
      id="description"
      value={formData.description}
      onChange={handleDescriptionChange}
      placeholder="Describe what this bot does"
      rows={3}
    />
  </div>
</div>

            {/* Keywords */}
            <div className={styles.sectionWide}>
              <h3>Trigger Keywords</h3>
              <div className={styles.field}>
                <label htmlFor="keywords">Add Keywords *</label>
                <div className={styles.keywordInputContainer}>
                  <input
                    ref={keywordInputRef}
                    id="keywords"
                    type="text"
                    value={keywordInput}
                    onChange={handleKeywordInputChange}
                    onKeyDown={handleKeywordInputKeyPress}
                    placeholder="Type keyword and press Enter"
                    className={errors.keywords ? styles.inputError : ''}
                  />
                  <button
                    type="button"
                    onClick={addKeywordFromInput}
                    className={styles.addKeywordBtn}
                    disabled={!keywordInput.trim()}
                  >
                    Add
                  </button>
                </div>
                <small className={styles.helpText}>
                  Type keywords and press Enter or comma to add them.
                </small>
                {errors.keywords && <span className={styles.errorText}>{errors.keywords}</span>}
              </div>

              {formData.keywords.length > 0 && (
                <div className={styles.keywordChipsContainer}>
                  <label>Keywords ({formData.keywords.length})</label>
                  <div className={styles.keywordChips}>
                    {formData.keywords.map((keyword, index) => (
                      <div key={index} className={styles.keywordChip}>
                        <span className={styles.keywordText}>{keyword}</span>
                        <button
                          type="button"
                          onClick={() => removeKeyword(keyword)}
                          className={styles.removeKeywordBtn}
                          title={`Remove "${keyword}"`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

        

{/* Template name */}
<div className={styles.rightColumn}>
  <div className={styles.sectionWide}>
    <h3>Response Template</h3>
    <div className={styles.field}>
      <label>Selected Template *</label>
      
      {templateLoading ? (
        <div className={styles.selectTemplateBtnWide}>
          <div className={styles.selectTemplateContent}>
            <div className={styles.spinner}></div>
            <span>Loading template preview...</span>
          </div>
        </div>
      ) : formData.selectedTemplate && templatePreviewData ? (
        <div className={styles.selectedTemplateWide}>
          <div className={styles.templatePreview}>
            <div className={styles.templateInfo}>
              <h4>{formData.selectedTemplate.name}</h4>
              <p className={styles.templateCategory}>WhatsApp Template</p>
              <div className={styles.templateMeta}>
                <span className={styles.templateStatus}>Active</span>
                <span className={styles.templateLanguage}>EN</span>
              </div>
            </div>
            <div className={styles.templateActions}>
              <button type="button" onClick={handleTemplateChange} className={styles.changeTemplateBtn}>
                Change Template
              </button>
            </div>
          </div>
          
          {/* WhatsApp Preview */}
          <div style={{ marginTop: '16px' }}>
            <WhatsAppPreview
              bodyValue={templatePreviewData.bodyValue}
              values={templatePreviewData.values}
              file={templatePreviewData.file}
            />
          </div>

          {/* 🔥 NEW: Personalization Fields Warning */}
          <div className={styles.personalizationWarning}>
            <div className={styles.warningHeader}>
              <i className="fas fa-info-circle" style={{ color: '#f59e0b' }}></i>
              <span>Template Personalization Notice</span>
            </div>
            <div className={styles.warningContent}>
              <p>
                <strong>Important:</strong> If this template contains personalization fields (like name and other details, etc.), 
                the bot may fail to send messages to new leads who don't have the required data fields populated.
              </p>
              <div className={styles.warningDetails}>
                <div className={styles.warningItem}>
                  <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                  <span><strong>Best Practice:</strong> Use templates with minimal or no personalization fields for automated bot responses</span>
                </div>
            
                
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowTemplateSelector(true)}
          className={`${styles.selectTemplateBtnWide} ${errors.template ? styles.btnError : ''}`}
        >
          <div className={styles.selectTemplateContent}>
            <i className="fa fa-plus-circle"></i>
            <span>Select Template</span>
            <small>Choose a WhatsApp template that auto-replies to keyword-based messages from your ads — great for sharing info or guiding users fast</small>
          </div>
        </button>
      )}
      
      {errors.template && <span className={styles.errorText}>{errors.template}</span>}
    </div>
  </div>
</div>
        </div>
      </form>

      {showTemplateSelector && (
        <UniversalTemplateSelector
          isOpen={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          onTemplateSelect={handleTemplateSelect}
          selectedTemplateId={formData.selectedTemplate?._id}
          templateType="whatsapp"
          handleDiscard={handleTemplateDiscard}
        />
      )}
    </div>
  ), [
    isEdit, onCancel, saving, templateLoading, handleSubmit, formData, errors,
    handleNameChange, handleActiveToggle, handleDescriptionChange,
    handleKeywordInputChange, handleKeywordInputKeyPress, addKeywordFromInput,
    removeKeyword, templatePreviewData, handleTemplateChange, showTemplateSelector,
    handleTemplateSelect, handleTemplateDiscard, keywordInput, hideHeader, isFormValid
  ]);

  // Loading state
  if (loading) {
    const LoadingContent = (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading bot data...</p>
      </div>
    );

    return hideHeader ? LoadingContent : (
      <div id="main" className="main">
        <Header />
        <Container fluid className="py-4">
          {LoadingContent}
        </Container>
      </div>
    );
  }

  return hideHeader ? formContent : (
    <div id="main" className="main">
      <Header />
      <Container fluid className="py-4">
        {formContent}
      </Container>
    </div>
  );
}, BotFormPropsAreEqual);

BotForm.displayName = 'BotForm';

export default BotForm;